import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    FaLightbulb,
    FaChartLine,
    FaFileSignature,
    FaHandshake,
    FaUserTie,
    FaMoneyBillWave,
    FaCheckCircle,
    FaBusinessTime,
    FaRocket,
    FaCheck
} from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 12px;

  @media (max-width: 1024px) {
    width: 90%;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
    width: 100%;
  }
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};

  &:hover {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color: #E60000;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const LeftSection = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  max-height: 400px;
  animation: ${slideInLeft} 1s ease-in-out;
  border-radius: 8px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease-in-out;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const FeatureCard = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  background: ${props => props.bgColor || 'linear-gradient(to right, #228B22, #E60000)'};
  color: white;
  font-size: 24px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
`;

const FeatureTitle = styled.h4`
  font-weight: bold;
  margin-bottom: 12px;
  color: #333;
  font-size: 20px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
 
  align-items: flex-start;
  margin-bottom: 15px;  // Augmenté pour plus d'espace vertical
  font-size: 16px;
  color: #555;
  
  svg {
    color: #228B22;
    margin-right: 10px;
    margin-top: 4px;
    flex-shrink: 0;
  }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  text-align: center;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
  }
  
  svg {
    font-size: 40px;
    margin-bottom: 15px;
  }
  
  p {
    font-weight: bold;
    margin-top: 10px;
    font-size: 18px;
  }
`;

const SectionTitle = styled.h3`
  position: relative;
  font-weight: bold;
  margin-bottom: 40px;
  padding-bottom: 15px;
  text-align: center;
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, #228B22, #E60000);
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const ContactCard = styled.div`
  background: linear-gradient(to right, #228B22, #E60000);
  color: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  margin: 40px auto 0 auto;
  text-align: center;
  width: 50%;
  display: block;

  @media (max-width: 768px) {
    width: 90%;
  }

  h4 {
    font-weight: bold;
    margin-bottom: 20px;
  }

  button {
    background: white;
    color: #333;
    border: none;
    padding: 10px 25px;
    border-radius: 6px;
    font-weight: bold;
    transition: all 0.3s ease;
    margin-top: 15px;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }
  }
`;

const StepCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 5px;
    background: linear-gradient(to bottom, #228B22, #E60000);
  }
`;

const StepNumber = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(to right, #228B22, #E60000);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const OfferCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  text-align: center;
  transition: transform 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-5px);
    border-color: linear-gradient(to right, #228B22, #E60000);
  }
`;

const CreateursRepreneurs = () => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container text-white text-start">
                    <h1 className="fw-bold">Création et Reprise d'Entreprise</h1>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/" className="text-white text-decoration-none">
                                    Accueil
                                </Link>{" "}
                                /{" "}
                                <Link to="/services" className="text-white text-decoration-none">
                                    Services
                                </Link>
                            </li>
                            <li className="breadcrumb-item active text-white" aria-current="page">
                                Création et Reprise d'Entreprise
                            </li>
                        </ol>
                    </nav>
                </div>
            </section>

            <section className="service-content py-5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 mx-auto text-center mb-5">
                            <h2 className="fw-bold">De l'Idée à la Réussite : Votre Projet, Notre Expertise</h2>
                            <div className="separator mx-auto my-3"></div>
                            <p className="lead text-muted text-justify">
                                Vous rêvez de créer votre entreprise ou de reprendre une activité existante ?
                                Chez CINEF-EC, nous savons que chaque projet est unique.
                                Nous vous offrons un accompagnement personnalisé à chaque étape,
                                de la validation de votre idée à la gestion quotidienne de votre entreprise,
                                pour maximiser vos chances de succès.. Nous collaborons étroitement avec vous pour concevoir et mettre en œuvre des stratégies de développement organisationnel, de structuration de projet et de mobilisation de ressources innovantes et adaptées à vos besoins spécifiques.
                                <br />Nous vous accompagnons à chaque étape de votre parcours, de la validation de votre idée à la gestion quotidienne de votre entreprise, pour maximiser vos chances de succès. Nous sommes bien plus qu'un cabinet d'expertise comptable : nous sommes votre partenaire de confiance.<br />
                                <br />Nous vous proposons un accompagnement sur mesure, adapté à vos besoins spécifiques, que vous soyez créateur ou repreneur
                            </p>
                        </div>
                    </div>

                    <div className="row mt-4">
                        <div className="col-md-6">
                            <FeatureCard>
                                <FeatureIcon bgColor="linear-gradient(135deg, #228B22, #32CD32)">
                                    <FaLightbulb />
                                </FeatureIcon>
                                <FeatureTitle>Une approche sur mesure</FeatureTitle>
                                <p>
                                    Chaque projet est unique. Nous adaptons notre accompagnement à vos besoins
                                    spécifiques pour garantir une exécution efficace et rentable de votre projet d'entreprise.
                                </p>
                            </FeatureCard>
                        </div>
                        <div className="col-md-6">
                            <FeatureCard>
                                <FeatureIcon bgColor="linear-gradient(135deg, #E60000, #FF6B6B)">
                                    <FaHandshake />
                                </FeatureIcon>
                                <FeatureTitle>Partenaire de confiance</FeatureTitle>
                                <p>
                                    Nous sommes bien plus qu'un cabinet d'expertise comptable : nous sommes votre
                                    partenaire de confiance qui vous accompagne à chaque étape de votre parcours entrepreneurial.
                                </p>
                            </FeatureCard>
                        </div>
                        <div className="col-md-6">
                            <FeatureCard>
                                <FeatureIcon bgColor="linear-gradient(135deg, #228B22, #32CD32)">
                                    <FaUserTie />
                                </FeatureIcon>
                                <FeatureTitle>Expertise pluridisciplinaire</FeatureTitle>
                                <p>
                                    Notre équipe réunit des experts dans tous les domaines nécessaires à la réussite
                                    de votre projet : comptabilité, fiscalité, droit, gestion, finance...
                                </p>
                            </FeatureCard>
                        </div>
                        <div className="col-md-6">
                            <FeatureCard>
                                <FeatureIcon bgColor="linear-gradient(135deg, #E60000, #FF6B6B)">
                                    <FaChartLine />
                                </FeatureIcon>
                                <FeatureTitle>Vision stratégique</FeatureTitle>
                                <p>
                                    Nous vous aidons à concevoir et mettre en œuvre des stratégies de développement
                                    adaptées à votre projet, pour une croissance durable de votre entreprise.
                                </p>
                            </FeatureCard>
                        </div>
                    </div>
                </div>
            </section>

            <section className="process-section py-5 bg-light">
                <div className="container">
                    <SectionTitle>Notre Accompagnement en 3 Étapes</SectionTitle>

                    <StepCard className="mb-4">
                        <StepNumber>1</StepNumber>
                        <FeatureTitle>Avant la Création/Reprise : Préparation et Validation du Projet</FeatureTitle>
                        <div className="row mt-3">
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Étude de marché</strong> : Analyse de votre marché, de la concurrence et validation du potentiel de votre projet
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Choix de la forme juridique</strong> : Conseils sur la structure la plus adaptée à votre situation
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Business plan complet</strong> : Présentation détaillée de votre projet et prévisionnel financier sur 3 à 5 ans
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                            <div className="col-md-12">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Recherche de financements</strong> : Identification des sources pertinentes et préparation des dossiers
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Pour les repreneurs : Audit d'acquisition</strong> : Analyse complète de l'entreprise cible
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Conseil personnalisé</strong> : Réponse à toutes vos questions sur la création/reprise
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                        </div>
                    </StepCard>

                    <StepCard className="mb-4">
                        <StepNumber>2</StepNumber>
                        <FeatureTitle>Création/Reprise : Formalités et Lancement</FeatureTitle>
                        <div className="row mt-3">
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Prise en charge des formalités administratives</strong> : Rédaction des statuts, immatriculation
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Démarches auprès des organismes sociaux et fiscaux</strong> : Simplification de vos démarches
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                            <div className="col-md-12">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Pour les repreneurs : Accompagnement dans les négociations</strong> : Conseil sur le prix, les conditions et les garanties
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                        </div>
                    </StepCard>

                    <StepCard>
                        <StepNumber>3</StepNumber>
                        <FeatureTitle>Après la Création/Reprise : Gestion et Développement</FeatureTitle>
                        <div className="row mt-3">
                            <div className="col-md-12">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Mise en place de votre comptabilité</strong> : Choix des outils et organisation administrative
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Tenue de la comptabilité</strong> : Saisie, déclarations de TVA, rapprochements bancaires
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Gestion de la paie et du social</strong> : Bulletins de paie, déclarations sociales
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Déclarations fiscales</strong> : Établissement et télétransmission de vos déclarations
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Tableaux de bord et suivi d'activité</strong> : Mise en place d'indicateurs clés de performance
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Conseil en gestion et stratégie</strong> : Accompagnement pour développer votre activité
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Optimisation fiscale et sociale</strong> : Réduction de vos charges
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                        </div>
                    </StepCard>
                </div>
            </section>

            <section className="services-section py-5">
                <div className="container">
                    <SectionTitle>Nos Services Spécifiques</SectionTitle>
                    <div className="row g-4">
                        <div className="col-md-4">
                            <ServiceCard>
                                <FaFileSignature className="text-success" />
                                <FeatureTitle>Business Plan</FeatureTitle>
                                <p className="text-muted">
                                    Élaboration d'un prévisionnel financier solide et convaincant pour vos partenaires
                                </p>
                            </ServiceCard>
                        </div>
                        <div className="col-md-4">
                            <ServiceCard>
                                <FaMoneyBillWave className="text-danger" />
                                <FeatureTitle>Financement</FeatureTitle>
                                <p className="text-muted">
                                    Recherche et mobilisation des financements adaptés à votre projet
                                </p>
                            </ServiceCard>
                        </div>
                        <div className="col-md-4">
                            <ServiceCard>
                                <FaBusinessTime className="text-primary" />
                                <FeatureTitle>Audit d'acquisition</FeatureTitle>
                                <p className="text-muted">
                                    Analyse approfondie de l'entreprise cible pour une reprise sécurisée
                                </p>
                            </ServiceCard>
                        </div>
                    </div>

                    <div className="row mt-4">
                        <div className="col">
                            <SectionTitle>Offres Spéciales Créateurs/Repreneurs</SectionTitle>
                            <div className="row g-4">
                                <div className="col-md-4">
                                    <OfferCard>
                                        <FaRocket className="text-success" style={{ fontSize: "40px", marginBottom: "15px" }} />
                                        <FeatureTitle>Pack Création/Reprise</FeatureTitle>
                                        <p className="text-muted">
                                            Une offre complète à tarif avantageux pour un accompagnement optimal dès le début
                                        </p>
                                    </OfferCard>
                                </div>
                                <div className="col-md-4">
                                    <OfferCard>
                                        <FaCheckCircle className="text-danger" style={{ fontSize: "40px", marginBottom: "15px" }} />
                                        <FeatureTitle>Tarifs réduits</FeatureTitle>
                                        <p className="text-muted">
                                            Des conditions tarifaires préférentielles pendant les premiers mois d'activité
                                        </p>
                                    </OfferCard>
                                </div>
                                <div className="col-md-4">
                                    <OfferCard>
                                        <FaHandshake className="text-primary" style={{ fontSize: "40px", marginBottom: "15px" }} />
                                        <FeatureTitle>Consultations gratuites</FeatureTitle>
                                        <p className="text-muted">
                                            Des rendez-vous réguliers pour faire le point sur votre situation et votre développement
                                        </p>
                                    </OfferCard>
                                </div>
                            </div>
                        </div>
                    </div>

                    <ContactCard>
                        <h4>Prêt à concrétiser votre projet ?</h4>
                        <p>
                            Contactez-nous dès aujourd'hui pour un entretien gratuit et sans engagement.
                            Nous étudierons ensemble votre projet et vous proposerons un accompagnement sur mesure
                            pour maximiser vos chances de succès.
                        </p>
                        <Link to="/businesscreationform" className="btn btn-danger px-4 py-2 fw-bold">
                            Contactez nos experts
                        </Link>
                    </ContactCard>
                </div>
            </section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.8), rgba(220, 53, 69, 0.8)), 
                      url("/assets/images/entrepreneur.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }
        
        .separator {
          height: 4px;
          width: 80px;
          background: linear-gradient(to right, #228B22, #E60000);
          border-radius: 2px;
        }
        
        .bg-light {
          background-color: #f8f9fa;
        }
        
        .lead {
          font-size: 1.2rem;
          line-height: 1.6;
        }
        
        #faq-section {
          transition: opacity 0.5s ease, transform 0.5s ease;
          opacity: 0;
          transform: translateY(20px);
        }
        
        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
        </>
    );
};

export default CreateursRepreneurs;